import { Coding, Patient } from '@medplum/fhirtypes';
import dayjs from 'dayjs';

export type PatientLanguageCode = 'en' | 'es' | 'OTH';

export const PATIENT_LANGUAGE_OPTIONS: {
  code: PatientLanguageCode;
  system: string;
  display: string;
}[] = [
  { code: 'en', system: 'urn:ietf:bcp:47', display: 'English' },
  { code: 'es', system: 'urn:ietf:bcp:47', display: 'Spanish' },
  { code: 'OTH', system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor', display: 'Other' },
];

const PATIENT_LANGUAGE_BY_CODE = Object.fromEntries(
  PATIENT_LANGUAGE_OPTIONS.map((option) => [option.code, option])
) as Record<PatientLanguageCode, (typeof PATIENT_LANGUAGE_OPTIONS)[number]>;

function isPatientLanguageCode(code: string | undefined): code is PatientLanguageCode {
  return code !== undefined && code in PATIENT_LANGUAGE_BY_CODE;
}

function getLanguageCodeFromEntry(
  entry: NonNullable<Patient['communication']>[number]
): PatientLanguageCode | undefined {
  const code = entry.language?.coding?.[0]?.code;
  return isPatientLanguageCode(code) ? code : undefined;
}

export function parsePatientLanguages(communication: Patient['communication'] | undefined): {
  selectedCodes: PatientLanguageCode[];
  preferredCode: PatientLanguageCode | undefined;
} {
  const entries = communication ?? [];
  const selectedCodes = entries
    .map((entry) => getLanguageCodeFromEntry(entry))
    .filter((code): code is PatientLanguageCode => code !== undefined);

  const preferredEntry = entries.find((entry) => entry.preferred);
  const preferredFromFlag = preferredEntry ? getLanguageCodeFromEntry(preferredEntry) : undefined;
  const preferredCode = preferredFromFlag ?? (selectedCodes.length > 0 ? selectedCodes[0] : undefined);

  return { selectedCodes, preferredCode };
}

export function buildPatientCommunication(
  selectedCodes: PatientLanguageCode[],
  preferredCode?: PatientLanguageCode
): NonNullable<Patient['communication']> {
  if (selectedCodes.length === 0) {
    return [];
  }

  const resolvedPreferred = selectedCodes.length === 1 ? selectedCodes[0] : (preferredCode ?? selectedCodes[0]);

  return selectedCodes.map((code) => {
    const option = PATIENT_LANGUAGE_BY_CODE[code];
    const coding: Coding = {
      system: option.system,
      code: option.code,
      display: option.display,
    };

    return {
      language: { coding: [coding] },
      preferred: code === resolvedPreferred,
    };
  });
}

export function formatPatientLanguagesDisplay(communication: Patient['communication'] | undefined): string {
  const { selectedCodes, preferredCode } = parsePatientLanguages(communication);

  if (selectedCodes.length === 0) {
    return 'No languages';
  }

  return selectedCodes
    .map((code) => {
      const label = PATIENT_LANGUAGE_BY_CODE[code].display;
      return code === preferredCode ? `${label} (Preferred)` : label;
    })
    .join(', ');
}

export function getPreferredLanguageDisplay(communication: Patient['communication'] | undefined): string | undefined {
  const { preferredCode } = parsePatientLanguages(communication);
  return preferredCode ? PATIENT_LANGUAGE_BY_CODE[preferredCode].display : undefined;
}

/**
 * Formats a birth date string with age calculation
 * @param dateString - Birth date string (e.g., "1990-01-15")
 * @returns Formatted string like "Jan 15, 1990 (34Y)" or "N/A" if invalid
 */
export function formatBirthDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A';
  const birthDate = dayjs(dateString);
  if (!birthDate.isValid()) return 'N/A';
  const age = dayjs().diff(birthDate, 'year');
  return `${birthDate.format('MMM D, YYYY')} (${age}Y)`;
}

/**
 * Formats a patient's gender value
 * @param gender - Patient gender value
 * @returns Capitalized gender string or "N/A" if not provided
 */
export function formatGender(gender: Patient['gender']): string {
  if (!gender) return 'N/A';
  return gender.charAt(0).toUpperCase() + gender.slice(1);
}

/**
 * Converts weight from kilograms to pounds
 * @param kg - Weight in kilograms
 * @returns Weight in pounds, rounded to nearest whole number
 */
export function kgToLbs(kg: number): number {
  return Math.round(kg * 2.20462);
}

/**
 * Converts height from centimeters to feet and inches format
 * @param cm - Height in centimeters
 * @returns Formatted string like "5'7""
 */
export function cmToFeetInches(cm: number): string {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return `${feet}'${inches}"`;
}
